import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import BackOffice, { useBackOffice } from "@/components/account/BackOffice";
import OrdersHome from "@/components/backoffice/OrdersHome";
import ImagesGallery from "@/components/backoffice/ImagesGallery";
import ProductsList from "@/components/backoffice/ProductsList";
import PayoutsScreen from "@/components/backoffice/PayoutsScreen";
import BrandingScreen from "@/components/backoffice/BrandingScreen";
import SettingsScreen from "@/components/backoffice/SettingsScreen";
import { Empty, Primary } from "@/components/backoffice/kit";

/* Phase 7 (8.1): the Simple bodies behind the existing routes — no new routes, `?tab=` picks the screen. */

function NeedShop({ children }) {
    const { data } = useBackOffice();
    if (!data) return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="animate-spin" /></div>;
    if (!data.store) {
        return (
            <Empty title="This screen comes with a shop." testid="bo-no-shop">
                <p className="mt-1 text-[15px]">Start your own print shop in a few minutes, or see your purchases.</p>
                <div className="mt-3 flex flex-wrap gap-2"><Primary to="/whitelabel" testid="bo-no-shop-start">Start your own print shop</Primary><Primary to="/account/purchases" testid="bo-no-shop-purchases">My orders</Primary></div>
            </Empty>
        );
    }
    return children;
}

export function SimpleOrdersPage() {
    const tab = new URLSearchParams(useLocation().search).get("tab");
    return <BackOffice><NeedShop>{tab === "images" ? <ImagesGallery /> : <OrdersHome />}</NeedShop></BackOffice>;
}

export function SimpleAccountHome() {
    return <BackOffice><HomeBody /></BackOffice>;
}

function HomeBody() {
    const { data } = useBackOffice();
    if (!data) return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="animate-spin" /></div>;
    if (!data.store) return <Navigate to="/account/purchases" replace />;
    return <OrdersHome />;
}

export function SimpleProductsPage() {
    return <BackOffice><NeedShop><ProductsList /></NeedShop></BackOffice>;
}

export function SimplePayoutsPage() {
    return <BackOffice><NeedShop><PayoutsScreen /></NeedShop></BackOffice>;
}

function StoreBody() {
    const tab = new URLSearchParams(useLocation().search).get("tab") || "branding";
    const { reload } = useBackOffice();
    const [store, setStore] = useState(null);
    const load = useCallback(() => api.get("/stores/me").then(({ data }) => setStore(data.store || {})).catch(() => setStore({})), []);
    useEffect(() => { load(); }, [load]);
    if (!store) return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="animate-spin" /></div>;
    const saved = (s) => { setStore(s); reload(); };
    return tab === "settings" ? <SettingsScreen store={store} onSaved={saved} /> : <BrandingScreen store={store} onSaved={saved} />;
}

export function SimpleStorePage() {
    return <BackOffice><NeedShop><StoreBody /></NeedShop></BackOffice>;
}
